import { Injectable, type Provider, signal } from '@angular/core';

import { type ApiResult, err, makeError, ok } from '../../../core/models/api-result';
import type { HubFeedItem, HubFeedLink } from './hub-feed.models';
import { normaliseLinkInput } from './hub-composer.steps';
import type { HubComposerDraft } from './hub-composer.models';
import {
  HubComposerService,
  type HubLiveIngest,
} from './hub-composer.service';
import { frameForWire, isCentred } from '../../../shared/media/image-frame';

/**
 * POSTING, WITHOUT A SESSION.
 *
 * `/preview/hubs` is guard-free so the surface can be reviewed and
 * screenshotted without logging in. Every read on that route is already
 * served from the prototype's corpus; the WRITE was the one thing still
 * pointed at the real API, which unauthenticated 401s. Without this the
 * composer could be opened in the harness and never completed — the visual
 * loop would be blind past the first step, which is most of the design.
 *
 * ── IT OVERRIDES TWO METHODS, DELIBERATELY ────────────────────────────────
 * `send` and `readBack`. The kind routing, the `posting` and `error` signals,
 * the null-hub refusal and the "a failed read-back is not a failed post" rule
 * are the REAL service's, inherited unchanged — so what the harness exercises
 * is the code that ships. Overriding `submit` would make this a second
 * implementation, and a harness that does not run the thing it is reviewing
 * goes green while the real path is broken.
 *
 * ── IT CAN BE MADE TO FAIL ────────────────────────────────────────────────
 * `failNext()` makes the next write return a `SERVER` error. The composer's
 * most important behaviour is what it does when a post does NOT go out — it
 * must keep the draft — and a harness that can only succeed cannot show that.
 * Drive it from the console: `ng.getComponent($0)` is awkward here, so the
 * preview page exposes it through a query parameter instead (`?composerFail=1`).
 */
@Injectable()
export class HubComposerPreviewService extends HubComposerService {
  /** Rows the harness has accepted, so a second post gets a second id. */
  private readonly written = signal<number>(0);

  /** When true, the NEXT write fails and then the flag clears itself. */
  private readonly _failNext = signal(false);

  constructor() {
    super();
    const params = new URLSearchParams(window.location.hash.split('?')[1] ?? '');
    if (params.get('composerFail') === '1') this.failNext();
  }

  failNext(): void {
    this._failNext.set(true);
  }

  protected override async send(
    draft: HubComposerDraft,
    hubId: string,
  ): Promise<ApiResult<{ readonly id: string } | null>> {
    // A visible pause. Instant success hides the posting state entirely, and
    // the disabled-while-posting behaviour is a thing reviewers need to see.
    await new Promise(resolve => setTimeout(resolve, 400));

    if (this._failNext()) {
      this._failNext.set(false);
      return err(
        makeError('SERVER', 'The hub did not accept that post.', {
          detail: 'Preview harness: forced failure, so the draft-retention behaviour can be reviewed.',
          retryable: true,
        }),
      );
    }

    // Build the input through the REAL builder even though nothing is sent.
    // It is the only way the harness exercises the wire mapping at all, and a
    // throw here (a poll with no options, say) should fail the preview too.
    this.buildPostInput(draft, hubId);

    const n = this.written() + 1;
    this.written.set(n);
    return ok({ id: `preview-post-${n}` });
  }

  /**
   * Build the tile the harness will show, from the draft itself.
   *
   * The real service asks the server for its own projection precisely because
   * a hand-mapped one would drift. That reasoning does not apply here: there
   * is no server, and the point is to show the reviewer a tile shaped like
   * the thing they just composed. It is a fixture, and it says so.
   */
  protected override async readBack(
    id: string,
    draft: HubComposerDraft,
  ): Promise<HubFeedItem | null> {
    // Matches the real service: neither of these is a ContentItem, so there
    // is nothing to put on the wall for them.
    if (draft.kind === 'live' || draft.kind === 'question') return null;

    const body = draft.text.trim();
    const link = draft.kind === 'link' ? this.previewLinkCard(draft) : null;
    const options = draft.pollOptions
      .map(option => option.trim())
      .filter(option => option.length > 0);

    return {
      id,
      kind: draft.kind === 'poll' ? 'POLL' : 'POST',
      format: this.previewFormat(draft),
      source: 'COMMUNITY',
      questionId: null,
      title: link ? (draft.linkTitle.trim() || link.title) : body.length > 0 ? body : null,
      body: body.length > 0 ? body : null,
      publishedAt: new Date().toISOString(),
      readTimeMinutes: null,
      pinned: false,
      viewerStarred: false,
      commentCount: 0,
      viewCount: 0,
      shareCount: 0,
      topics: [...draft.labels],
      hub: {
        _id: draft.hubId ?? 'preview-hub',
        name: 'This hub',
        type: 'CUSTOM',
        primaryColor: null,
        memberCount: null,
        logo: null,
        memberRole: null,
      },
      author: {
        _id: 'preview-viewer',
        firstName: 'You',
        lastName: null,
        jobTitle: null,
        picture: null,
      },
      media: draft.media.length
        ? {
            primaryKind: draft.media[0].kind === 'clip' ? 'VIDEO' : 'IMAGE',
            image:
              draft.media[0].kind === 'photo'
                ? {
                    ...this.previewAsset(draft.media[0].previewUrl),
                    // Kept as the server keeps it (Position, 10 Oct 2026).
                    frame:
                      draft.media[0].frame && !isCentred(draft.media[0].frame)
                        ? frameForWire(draft.media[0].frame)
                        : null,
                  }
                : null,
            video:
              draft.media[0].kind === 'clip'
                ? this.previewAsset(draft.media[0].previewUrl)
                : null,
            gallery: draft.media
              .slice(1)
              .map(item => this.previewAsset(item.previewUrl)),
          }
        : null,
      reactions: { count: 0, viewerHasReacted: false, viewerReaction: null },
      poll:
        draft.kind === 'poll'
          ? {
              question: body,
              // Positional ids, the same way `buildPollDoc` mints them, so the
              // vote control in the harness addresses something real.
              options: options.map((label, i) => ({
                id: `opt${i + 1}`,
                label,
                voteCount: 0,
              })),
              allowMultiple: draft.pollAllowMultiple,
              closesAt: null,
              closed: false,
              totalVotes: 0,
            }
          : null,
      viewerVotedOptionIds: [],
      link,
    };
  }

  /**
   * The link card, in the harness: the page cannot be fetched here, so the
   * domain stands in for everything, as it does on dev for a page the server
   * could not read.
   */
  protected override async fetchLinkPreview(url: string): Promise<HubFeedLink | null> {
    await new Promise(resolve => setTimeout(resolve, 300));
    const domain = new URL(url).hostname.replace(/^www\./, '');
    return {
      url,
      domain,
      title: `A page on ${domain}`,
      description: 'The page’s own description appears here when the server can read it.',
      imageUrl: null,
      siteName: null,
    };
  }

  private previewLinkCard(draft: HubComposerDraft): HubFeedLink | null {
    const url = normaliseLinkInput(draft.linkUrl);
    if (!url) return null;
    const domain = new URL(url).hostname.replace(/^www\./, '');
    return { url, domain, title: `A page on ${domain}`, description: null, imageUrl: null, siteName: null };
  }

  private previewFormat(draft: HubComposerDraft): HubFeedItem['format'] {
    if (draft.kind === 'poll') return 'POLL';
    if (draft.kind === 'short') return 'SHORT';
    if (draft.kind === 'link') return 'LINK';
    const first = draft.media[0];
    if (!first) return 'UPDATE';
    return first.kind === 'clip' ? 'VIDEO' : 'PHOTO';
  }

  /**
   * A STREAM KEY THAT IS OBVIOUSLY NOT ONE.
   *
   * `liveEventIngest` is gated on `live-event:broadcast`, so unauthenticated
   * it 401s and the key step would be unreviewable — which is most of the
   * OWNED path. The fixture says what it is in the value itself: nobody
   * pastes this into OBS by accident, and nobody mistakes it for a leak.
   */
  protected override async ingest(
    eventId: string,
  ): Promise<HubLiveIngest | null> {
    await new Promise(resolve => setTimeout(resolve, 350));
    return {
      ingestUrl: 'rtmps://global-live.mux.com:443/app',
      streamKey: `preview-fixture-not-a-real-key-${eventId}`,
    };
  }

  /**
   * ⚠ THE UPLOAD SEAM, AND IT WAS MISSING. `uploadAttachment` was added to the
   * base service with a docblock saying it is `protected` so this harness can
   * replace it — and then not replaced here. Attaching a photograph on
   * `/preview/hubs` would have called the real `/api/files/upload`, 401'd
   * against a guard-free route, and made the whole attach step unreviewable:
   * exactly the failure the seam exists to prevent, reintroduced by only doing
   * half of it.
   *
   * Delayed on purpose. The tray renders an "Uploading…" state per picture,
   * and an instant resolve would mean nobody ever sees it — a state that
   * cannot be observed in the harness is a state nobody reviews.
   */
  protected override async uploadAttachment(file: File): Promise<string | null> {
    await new Promise(resolve => setTimeout(resolve, 900));
    // A plausible durable URL, in the shape the real door returns.
    return `https://storage.googleapis.com/preview-bucket/hub-post/${encodeURIComponent(
      file.name,
    )}`;
  }

  private previewAsset(url: string) {
    return {
      url,
      posterUrl: null,
      // Null, as it is for a real gallery entry — the server stores no ratio
      // for one, so a harness that invented one would hide the layout problem
      // that absence causes.
      aspectRatio: null,
      durationSeconds: null,
      tier: null,
      fit: null,
      playbackId: null,
    };
  }
}

/** Drop into a route's `providers` to accept posts instead of sending them. */
export const HUB_COMPOSER_PREVIEW_PROVIDER: Provider = {
  provide: HubComposerService,
  useClass: HubComposerPreviewService,
};
